import { useEffect, useState, type JSX } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import {
  type CreatePersonnelDetailsRequest,
  type PersonnelDetailsDto,
  type UpdatePersonnelDetailsRequest,
} from '@ctms/contracts';
import { z } from 'zod';
import { AlertCircle, TriangleAlert, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../../trials/trials.api.js';

export type ContactEditor = { mode: 'create' } | { mode: 'edit'; id: number };

export interface ContactCardDialogProps {
  editor: ContactEditor | null;
  canWrite: boolean;
  onClose: () => void;
  onSaved: (card: PersonnelDetailsDto) => void;
  /** Dialog heading. Defaults to the contacts-catalog wording. */
  title?: string;
  /** Submit button label. Defaults to the contacts-catalog wording. */
  submitLabel?: string;
  /**
   * Create-mode only: returns a non-blocking warning for the current draft, or null.
   * The caller owns the wording; the dialog never prevents submission.
   */
  duplicateWarning?: (draft: { firstName: string; lastName: string }) => string | null;
}

const ContactCardFormSchema = z
  .object({
    lastName: z.string().trim().min(1, 'Nazwisko jest wymagane').max(255),
    firstName: z.string().trim().min(1, 'Imię jest wymagane').max(255),
    internalPersonnel: z.boolean(),
    phoneMobile: z.string().max(50),
    phone2: z.string().max(50),
    email1: z.string().max(320),
    email1Comments: z.string().max(10_000),
    email2: z.string().max(320),
    email2Comments: z.string().max(10_000),
    comments: z.string().max(10_000),
  })
  .superRefine((value, ctx) => {
    for (const path of ['email1', 'email2'] as const) {
      const trimmed = value[path].trim();
      if (trimmed.length === 0) {
        continue;
      }
      if (!z.email().safeParse(trimmed).success) {
        ctx.addIssue({
          code: 'custom',
          message: 'Niepoprawny format adresu e-mail',
          path: [path],
        });
      }
    }
  });

type ContactCardFormInput = z.infer<typeof ContactCardFormSchema>;

const EMPTY_FORM: ContactCardFormInput = {
  lastName: '',
  firstName: '',
  internalPersonnel: false,
  phoneMobile: '',
  phone2: '',
  email1: '',
  email1Comments: '',
  email2: '',
  email2Comments: '',
  comments: '',
};

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

function filled(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function textOrEmpty(value: string | null): string {
  return value ?? '';
}

function formFromCard(card: PersonnelDetailsDto): ContactCardFormInput {
  return {
    lastName: textOrEmpty(card.lastName),
    firstName: textOrEmpty(card.firstName),
    internalPersonnel: card.internalPersonnel,
    phoneMobile: textOrEmpty(card.phoneMobile),
    phone2: textOrEmpty(card.phone2),
    email1: textOrEmpty(card.email1),
    email1Comments: textOrEmpty(card.email1Comments),
    email2: textOrEmpty(card.email2),
    email2Comments: textOrEmpty(card.email2Comments),
    comments: textOrEmpty(card.comments),
  };
}

function toCreateRequest(data: ContactCardFormInput): CreatePersonnelDetailsRequest {
  const request: CreatePersonnelDetailsRequest = {
    lastName: data.lastName.trim(),
    firstName: data.firstName.trim(),
    internalPersonnel: data.internalPersonnel,
  };
  const phoneMobile = filled(data.phoneMobile);
  const phone2 = filled(data.phone2);
  const email1 = filled(data.email1);
  const email2 = filled(data.email2);
  const email1Comments = filled(data.email1Comments);
  const email2Comments = filled(data.email2Comments);
  const comments = filled(data.comments);
  if (phoneMobile) {
    request.phoneMobile = phoneMobile;
  }
  if (phone2) {
    request.phone2 = phone2;
  }
  if (email1) {
    request.email1 = email1;
  }
  if (email2) {
    request.email2 = email2;
  }
  if (email1Comments) {
    request.email1Comments = email1Comments;
  }
  if (email2Comments) {
    request.email2Comments = email2Comments;
  }
  if (comments) {
    request.comments = comments;
  }
  return request;
}

function toUpdateRequest(data: ContactCardFormInput): UpdatePersonnelDetailsRequest {
  return {
    lastName: data.lastName.trim(),
    firstName: data.firstName.trim(),
    internalPersonnel: data.internalPersonnel,
    phoneMobile: filled(data.phoneMobile) ?? null,
    phone2: filled(data.phone2) ?? null,
    email1: filled(data.email1) ?? null,
    email2: filled(data.email2) ?? null,
    email1Comments: filled(data.email1Comments) ?? null,
    email2Comments: filled(data.email2Comments) ?? null,
    comments: filled(data.comments) ?? null,
  };
}

function TextField({
  id,
  label,
  error,
  registration,
  disabled,
  multiline = false,
}: {
  id: string;
  label: string;
  error?: string;
  registration: UseFormRegisterReturn;
  disabled: boolean;
  multiline?: boolean;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium uppercase tracking-wider text-slate-300">
        {label}
      </label>
      {multiline ? (
        <textarea id={id} rows={2} disabled={disabled} className={fieldClass} {...registration} />
      ) : (
        <input id={id} type="text" disabled={disabled} className={fieldClass} {...registration} />
      )}
      {error ? <p className="mt-1 text-xs text-rose-400">{error}</p> : null}
    </div>
  );
}

export function ContactCardDialog({
  editor,
  canWrite,
  onClose,
  onSaved,
  title = 'Karta kontaktowa',
  submitLabel = 'Zapisz kartę',
  duplicateWarning,
}: ContactCardDialogProps): JSX.Element | null {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isOpen = editor !== null;
  const editingId = editor?.mode === 'edit' ? editor.id : null;

  const cardQuery = useQuery({
    queryKey: ['personnel', 'detail', editingId],
    queryFn: () => trialsApi.getPersonnel(editingId ?? 0),
    enabled: editingId !== null,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ContactCardFormInput>({
    resolver: zodResolver(ContactCardFormSchema),
    defaultValues: EMPTY_FORM,
  });

  const isCreate = editor?.mode === 'create';
  const duplicateMessage =
    duplicateWarning && isCreate
      ? duplicateWarning({ firstName: watch('firstName'), lastName: watch('lastName') })
      : null;

  useEffect(() => {
    if (editor?.mode === 'create') {
      reset(EMPTY_FORM);
      return;
    }
    if (cardQuery.data) {
      reset(formFromCard(cardQuery.data));
    }
  }, [cardQuery.data, editor, reset]);

  if (!isOpen || !editor) {
    return null;
  }

  const isLoadingCard = editor.mode === 'edit' && cardQuery.isLoading;
  const fieldsDisabled = isSubmitting || isLoadingCard || !canWrite;

  const handleClose = (): void => {
    if (isSubmitting) {
      return;
    }
    reset(EMPTY_FORM);
    setErrorMessage(null);
    onClose();
  };

  const onSubmit = async (data: ContactCardFormInput): Promise<void> => {
    if (!canWrite) {
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const card =
        editor.mode === 'create'
          ? await trialsApi.createPersonnel(toCreateRequest(data))
          : await trialsApi.updatePersonnel(editor.id, toUpdateRequest(data));
      reset(EMPTY_FORM);
      onSaved(card);
      onClose();
    } catch (err: unknown) {
      const message = await getApiErrorMessage(err, 'Nie udało się zapisać karty kontaktowej.');
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-card-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-xs"
    >
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div>
            <h2 id="contact-card-title" className="text-base font-semibold text-slate-100">
              {title}
            </h2>
            <p className="text-xs text-slate-400">Nazwisko i imię są wymagane. Telefony i e-maile są opcjonalne.</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isLoadingCard ? (
          <p className="py-8 text-center text-sm text-slate-400">Ładowanie karty</p>
        ) : cardQuery.isError ? (
          <section role="alert" className="mt-5">
            <h3 className="text-sm font-semibold text-rose-200">Nie udało się wczytać karty</h3>
            <button
              type="button"
              onClick={() => {
                void cardQuery.refetch();
              }}
              className="mt-3 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
            >
              Spróbuj ponownie
            </button>
          </section>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
            {errorMessage ? (
              <div
                role="alert"
                className="flex items-center gap-2.5 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
              >
                <AlertCircle className="h-4 w-4 shrink-0 text-rose-400" />
                <span>{errorMessage}</span>
              </div>
            ) : null}

            {duplicateMessage ? (
              <div
                role="status"
                className="flex items-center gap-2.5 rounded-md border border-amber-800/60 bg-amber-950/40 p-3 text-sm text-amber-300"
              >
                <TriangleAlert className="h-4 w-4 shrink-0" />
                <span>{duplicateMessage}</span>
              </div>
            ) : null}

            <TextField
              id="contact-last-name"
              label="Nazwisko"
              error={errors.lastName?.message}
              registration={register('lastName')}
              disabled={fieldsDisabled}
            />
            <TextField
              id="contact-first-name"
              label="Imię"
              error={errors.firstName?.message}
              registration={register('firstName')}
              disabled={fieldsDisabled}
            />
            <label className="mt-1 flex items-center gap-2 text-sm text-slate-300">
              <input
                id="contact-internal-personnel"
                type="checkbox"
                disabled={fieldsDisabled}
                className="h-3.5 w-3.5 rounded-sm border-slate-700"
                {...register('internalPersonnel')}
              />
              Pracownik ośrodka
            </label>
            <TextField
              id="contact-phone-mobile"
              label="Telefon komórkowy"
              registration={register('phoneMobile')}
              disabled={fieldsDisabled}
            />
            <TextField
              id="contact-phone-2"
              label="Drugi telefon"
              registration={register('phone2')}
              disabled={fieldsDisabled}
            />
            <TextField
              id="contact-email-1"
              label="E-mail 1"
              error={errors.email1?.message}
              registration={register('email1')}
              disabled={fieldsDisabled}
            />
            <TextField
              id="contact-email-1-comments"
              label="Komentarz do e-maila 1"
              registration={register('email1Comments')}
              disabled={fieldsDisabled}
              multiline
            />
            <TextField
              id="contact-email-2"
              label="E-mail 2"
              error={errors.email2?.message}
              registration={register('email2')}
              disabled={fieldsDisabled}
            />
            <TextField
              id="contact-email-2-comments"
              label="Komentarz do e-maila 2"
              registration={register('email2Comments')}
              disabled={fieldsDisabled}
              multiline
            />
            <TextField
              id="contact-comments"
              label="Komentarz ogólny"
              registration={register('comments')}
              disabled={fieldsDisabled}
              multiline
            />

            {!canWrite ? <p className="text-xs text-amber-300">Nie masz uprawnienia do zapisu kontaktów.</p> : null}

            <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !canWrite}
                className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? 'Zapisywanie…' : submitLabel}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
